import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import axios from 'axios';
import { getApiUrl } from '../config';

/**
 * Decode a JWT token and extract its payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} - The decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if a token is expired based on stored issue time and expires_in
 * @returns {boolean} - True if token is valid, false if expired or missing
 */
export const isTokenValid = () => {
  const token = Cookies.get('access_token');
  const tokenIssuedAt = Cookies.get('token_issued_at');

  if (!token || !tokenIssuedAt) {
    return false;
  }

  try {
    // Decode the token to get expires_in (in minutes)
    const decoded = decodeToken(token);

    if (!decoded) {
      return false;
    }

    // Check if token has exp claim (standard JWT expiration)
    if (decoded.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp > currentTime;
    }

    // Fallback: check using stored issue time and expires_in from token
    if (decoded.expires_in) {
      const issuedAt = parseInt(tokenIssuedAt, 10);
      const expiresInMs = decoded.expires_in * 60 * 1000; // Convert minutes to milliseconds
      const expirationTime = issuedAt + expiresInMs;
      const currentTime = Date.now();

      return currentTime < expirationTime;
    }

    // If no expiration info found, consider token invalid
    return false;
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};

/**
 * Attributes for the auth cookies.
 *
 * `sameSite: 'strict'` unconditionally: the token is read back by JS and
 * replayed in the Authorization header, so SameSite never gates our own API
 * calls — it only keeps the cookie off cross-site requests, which costs us
 * nothing in either environment.
 *
 * `secure` only over HTTPS, because a browser silently drops a Secure cookie
 * set on a plain-HTTP page — which is how the app is served in development
 * (`just run`, reachable as localhost, 192.168.x.x, 10.100.x.x, …). Keyed on
 * the actual protocol rather than NODE_ENV so a production build served over
 * HTTP locally still logs in, and any HTTPS deployment gets the flag.
 */
const authCookieOptions = () => ({
  expires: 7,
  sameSite: 'strict',
  ...(window.location.protocol === 'https:' ? { secure: true } : {}),
});

/**
 * Save authentication token and issue timestamp
 * @param {string} token - The JWT access token
 */
export const saveAuthToken = (token) => {
  const now = Date.now();
  const options = authCookieOptions();

  // Save token (expires in 7 days as cookie expiration, but we'll check JWT expiration separately)
  Cookies.set('access_token', token, options);

  // Save the timestamp when token was issued
  Cookies.set('token_issued_at', now.toString(), options);
};

// Sign-out hooks installed by submodules (e.g. `playbackSyncBootstrap.js` and
// `voiceCatalogue.js`) after they are imported. Stored as a Set so multiple
// submodules can register independently; each hook runs in registration
// order and failures are isolated to keep logout strictly non-blocking.
const signOutCleanupHooks = new Set();

let cachedUserId = null;

/**
 * Where the signed-in user's own id is remembered between sessions.
 *
 * Namespaced by the token's `sub` so one account cannot read another's on a
 * shared browser. This is the user's own identifier, not a credential — the
 * token in the cookie already carries it — so it is not the sensitive data
 * Constitution III keeps out of localStorage.
 */
const userIdKey = () => {
  const token = getAccessToken();
  const decoded = token ? decodeToken(token) : null;
  return decoded && decoded.sub ? `dissona-user-id::${decoded.sub}` : null;
};

const readPersistedUserId = () => {
  try {
    const key = userIdKey();
    return key ? localStorage.getItem(key) : null;
  } catch (e) {
    return null;
  }
};

const persistUserId = (id) => {
  try {
    const key = userIdKey();
    if (key && id) localStorage.setItem(key, id);
  } catch (e) {
    console.warn('could not remember user identity', e);
  }
};

const forgetPersistedUserId = () => {
  try {
    const key = userIdKey();
    if (key) localStorage.removeItem(key);
  } catch (e) {
    /* nothing to clean up */
  }
};

/**
 * The signed-in user's own id.
 *
 * Falls back to the remembered value rather than depending on
 * `fetchAndCacheMe()` having completed first. Three screens compare this
 * against `owner_id` to decide whether the user owns a project, and a null
 * answer does not read as "unknown" — it reads as "not yours", which hides the
 * owner's controls and labels their own project as shared with them.
 *
 * The token cannot answer this: its `sub` is the username, while `owner_id` is
 * a uuid, so they are different values.
 */
export const getCachedUserId = () => {
  if (!cachedUserId) cachedUserId = readPersistedUserId();
  return cachedUserId;
};

export const clearCachedUserId = () => { cachedUserId = null; };

/**
 * Resolve the signed-in user's own id.
 *
 * Offline, `auth/me` cannot be reached, and without a remembered answer the
 * user is treated as nobody — which makes every project they own look like one
 * shared with them, and hides the controls that go with ownership. So the last
 * known answer is remembered and used immediately, then corrected from the
 * server whenever it can be reached.
 */
export const fetchAndCacheMe = async () => {
  if (cachedUserId) return cachedUserId;
  const token = getAccessToken();
  if (!token) return null;

  cachedUserId = readPersistedUserId();

  try {
    const res = await axios.get(getApiUrl('auth/me'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.data?.id) {
      cachedUserId = res.data.id;
      persistUserId(cachedUserId);
    }
  } catch (err) {
    console.warn('fetchAndCacheMe: could not fetch user identity', err?.message);
  }
  return cachedUserId;
};

/**
 * Register a function to run before the cookie clear in `logout()`. Additive:
 * every registered hook fires on logout. Returns an unregister function the
 * caller can use to detach the hook (useful in tests).
 */
export const registerSignOutCleanup = (fn) => {
  if (typeof fn !== 'function') return () => {};
  signOutCleanupHooks.add(fn);
  return () => { signOutCleanupHooks.delete(fn); };
};

/**
 * Clear authentication tokens and redirect to login.
 *
 * Runs every registered sign-out hook first; per-hook failures are swallowed
 * (and logged) so logout stays strictly non-blocking even if one cleanup
 * throws.
 */
export const clearSession = () => {
  // Before the cookies go: the storage key is derived from the token.
  forgetPersistedUserId();
  for (const hook of Array.from(signOutCleanupHooks)) {
    try {
      const result = hook();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (e) {
      console.warn('logout: signOutCleanup hook failed', e);
    }
  }
  clearCachedUserId();
  Cookies.remove('access_token');
  Cookies.remove('token_issued_at');
};

export const logout = () => {
  clearSession();
  window.location.href = '/login';
};

/**
 * Get the current access token
 * @returns {string|null} - The access token or null if not found
 */
export const getAccessToken = () => {
  return Cookies.get('access_token') || null;
};

/**
 * Whether the current session should still open the app.
 *
 * FR-025: offline, an expired token means "cannot sync", not "cannot use the
 * app" — a user must not be evicted from content already on their device
 * because a renewal could not happen. Renewal is deferred until connectivity
 * returns; only an explicit server rejection signs them out.
 *
 * A device with no token at all is signed out regardless of connectivity.
 *
 * @returns {boolean} - True if the app should render for this session
 */
export const isSessionUsable = () => {
  if (isTokenValid()) return true;
  return !!getAccessToken() && navigator.onLine === false;
};

/**
 * Check token validity and redirect to login if expired
 * @returns {boolean} - True if token is valid, false otherwise
 */
export const requireAuth = () => {
  if (!isTokenValid()) {
    logout();
    return false;
  }
  return true;
};

